import { CommonModule } from "@angular/common";
import { Component } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { RouterLink } from "@angular/router";

type FaqCategoryKey =
  | "booking"
  | "cancellations"
  | "payments"
  | "accounts"
  | "finding"
  | "reviews"
  | "notifications"
  | "owners"
  | "safety"
  | "support";

interface FaqItem {
  id: string;
  category: FaqCategoryKey;
  question: string;
  answer: string;
}

interface FaqCategory {
  key: "all" | FaqCategoryKey;
  label: string;
}

interface FaqGroup {
  key: FaqCategoryKey;
  label: string;
  items: FaqItem[];
}

@Component({
  selector: "app-faq",
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink],
  templateUrl: "./faq.component.html",
  styleUrl: "./faq.component.scss",
})
export class FaqComponent {
  searchTerm = "";
  activeCategory: "all" | FaqCategoryKey = "all";
  openFaqIds = new Set<string>();

  categories: FaqCategory[] = [
    { key: "all", label: "All" },
    { key: "booking", label: "Booking" },
    { key: "cancellations", label: "Cancellations" },
    { key: "payments", label: "Payments" },
    { key: "accounts", label: "Accounts" },
    { key: "finding", label: "Finding salons" },
    { key: "reviews", label: "Reviews" },
    { key: "notifications", label: "Notifications" },
    { key: "owners", label: "For salon owners" },
    { key: "safety", label: "Safety & privacy" },
    { key: "support", label: "Technical support" },
  ];

  faqs: FaqItem[] = [
    {
      id: "book-online",
      category: "booking",
      question: "How do I book an appointment online?",
      answer:
        "Search for a salon or service in your area, choose your preferred provider or service, select your location, pick an available date and time, and confirm your booking. You will receive a confirmation by email and/or SMS, and the appointment will also appear in your booking dashboard.",
    },
    {
      id: "same-day",
      category: "booking",
      question: "Can I book an appointment for the same day?",
      answer:
        "Yes. Same-day bookings are available depending on the salon's availability. Search for your preferred service and filter by today's date, or use the Available Now filter on the home page.",
    },
    {
      id: "advance-booking",
      category: "booking",
      question: "How far in advance can I schedule an appointment?",
      answer:
        "Most salons allow bookings up to three months in advance, although the booking window can vary by provider. Check the salon's booking calendar for its current availability.",
    },
    {
      id: "multiple-services",
      category: "booking",
      question: "Can I book multiple services in one appointment?",
      answer:
        "Yes. Add multiple treatments while building your booking. gloget will calculate the combined duration and show appointment times that can accommodate the selected services.",
    },
    {
      id: "booking-confirmed",
      category: "booking",
      question: "How do I know my booking is confirmed?",
      answer:
        'Once the booking is complete, you will receive a confirmation email and/or SMS with the appointment details. You can also view upcoming bookings in the "My Appointments" section of your account.',
    },
    {
      id: "book-for-someone",
      category: "booking",
      question: "Can I book an appointment on behalf of someone else?",
      answer:
        "Yes. During booking, enter the details of the person the appointment is for so the salon knows who to expect. The appointment will still appear in your customer dashboard.",
    },
    {
      id: "minimum-age",
      category: "booking",
      question: "Is there a minimum age requirement for certain services?",
      answer:
        "Some services, including certain chemical treatments or skin procedures, may require clients to be 16 or 18 years old. Restrictions are set by the individual salon and should be shown with the service information.",
    },
    {
      id: "cancel-reschedule",
      category: "cancellations",
      question: "How do I cancel or reschedule my appointment?",
      answer:
        'Open "My Appointments" in your account, select the booking you want to change, and follow the prompts. You may also be able to use the link in your confirmation email.',
    },
    {
      id: "cancellation-policy",
      category: "cancellations",
      question: "What is the cancellation policy?",
      answer:
        "Cancellation policies vary by salon. Many require at least 24 to 48 hours' notice to cancel or reschedule without a fee. The applicable policy is displayed on the salon profile and during checkout.",
    },
    {
      id: "late-cancel",
      category: "cancellations",
      question: "Will I be charged for a late cancellation or no-show?",
      answer:
        "You may be charged if you cancel outside the salon's cancellation window or do not attend the appointment. Any cancellation or no-show fee is set by the salon and communicated during booking.",
    },
    {
      id: "salon-cancels",
      category: "cancellations",
      question: "What happens if the salon cancels my appointment?",
      answer:
        "You will be notified by email or SMS. Any deposit or payment made through the platform will be refunded in full, and you can then rebook for another suitable time.",
    },
    {
      id: "payment-methods",
      category: "payments",
      question: "What payment methods are accepted?",
      answer:
        "Major credit and debit cards are accepted, along with supported digital wallets such as Apple Pay and Google Pay. Some providers may also accept cash on the day, which will be indicated on their profile.",
    },
    {
      id: "booking-deposit",
      category: "payments",
      question: "Do I need to pay a deposit to secure my booking?",
      answer:
        "Some salons require a deposit at the time of booking. If a deposit applies, it will be shown before you confirm and is typically deducted from the final amount due.",
    },
    {
      id: "listed-prices",
      category: "payments",
      question: "Are the listed prices inclusive of all fees?",
      answer:
        "Service prices are set by the individual salon and should reflect the service cost. Some salons may charge extra for certain hair lengths, treatments, or add-ons; where applicable, those charges should be noted on the salon page.",
    },
    {
      id: "gift-cards",
      category: "payments",
      question: "Do you offer gift cards or vouchers?",
      answer:
        "Yes. Digital gloget gift cards can be purchased and redeemed at participating salons. Available denominations and redemption details are shown when purchasing a gift card.",
    },
    {
      id: "service-refund",
      category: "payments",
      question: "Can I get a refund if I'm not satisfied with the service?",
      answer:
        "Refund requests for completed services are handled directly by the salon. Where possible, raise any concern with the salon at the time of your appointment so they have an opportunity to address it.",
    },
    {
      id: "discounts",
      category: "payments",
      question: "Are student, senior, or loyalty discounts available?",
      answer:
        "Some salons offer discounts for students, seniors, or returning clients. Offers may be shown on the salon profile or applied with a promo code at checkout. gloget rewards may also be available for eligible services.",
    },
    {
      id: "account-required",
      category: "accounts",
      question: "Do I need an account to book an appointment?",
      answer:
        "You can browse salons and services without an account, but a free account is required to complete a booking. Your account lets you manage appointments, save favourite salons, receive reminders, collect points, and protect your booking information.",
    },
    {
      id: "update-profile",
      category: "accounts",
      question: "How do I update my personal information?",
      answer:
        'Once signed in, open "Account Settings" to update your name, email address, phone number, and other profile details.',
    },
    {
      id: "reset-password",
      category: "accounts",
      question: "How do I reset my password?",
      answer:
        'Select "Forgot Password" on the login page and enter your registered email address. You will receive a password reset link. If it does not arrive, check your spam or junk folder.',
    },
    {
      id: "booking-history",
      category: "accounts",
      question: "Can I view my booking history?",
      answer:
        'Yes. Past and upcoming appointments are available in the "My Appointments" section of your customer dashboard.',
    },
    {
      id: "delete-account",
      category: "accounts",
      question: "How do I delete my account?",
      answer:
        'You can request account deletion through "Account Settings." Account deletion permanently removes your account data and booking history, subject to any required retention. Cancel active bookings before requesting deletion.',
    },
    {
      id: "salons-near-me",
      category: "finding",
      question: "How do I search for salons near me?",
      answer:
        "Enter your location or allow location access, then browse or search for the service you need. Search results can be narrowed using filters such as distance, rating, price range, and availability.",
    },
    {
      id: "filter-salons",
      category: "finding",
      question: "Can I filter salons by service type, price, or rating?",
      answer:
        "Yes. Use the filters and sorting options on the search results page to narrow salons by category, price range, star rating, and availability.",
    },
    {
      id: "choose-stylist",
      category: "finding",
      question: "How do I know which stylist or staff member is right for me?",
      answer:
        'Salon profiles may show individual team members with their specialties, experience, and photos. You can select a specific professional when booking or choose "No preference" where that option is available.',
    },
    {
      id: "verified-profiles",
      category: "finding",
      question: "Are salon profiles and reviews verified?",
      answer:
        "Salons listed on gloget go through a verification process before going live. Reviews may come from Google or gloget customers who completed a booking with that provider.",
    },
    {
      id: "salon-photos",
      category: "finding",
      question: "Can I see a salon's portfolio or photos before booking?",
      answer:
        "Yes. Salon profiles can include a gallery showing the premises and examples of their work. Some individual stylists may also have their own portfolio content.",
    },
    {
      id: "leave-review",
      category: "reviews",
      question: "How do I leave a review after my appointment?",
      answer:
        'After your appointment, you may receive an email or notification asking for a review. You can also open "My Appointments," locate the completed booking, and select "Leave a Review."',
    },
    {
      id: "salon-review-response",
      category: "reviews",
      question: "Can a salon respond to my review?",
      answer:
        "Yes. Salons can respond publicly to reviews. gloget encourages respectful and constructive communication between customers and service providers.",
    },
    {
      id: "rating-calculation",
      category: "reviews",
      question: "How are salon ratings calculated?",
      answer:
        "Ratings are calculated from customer review scores, including eligible gloget and Google reviews, and are displayed on a five-star scale.",
    },
    {
      id: "fake-review",
      category: "reviews",
      question: "What should I do if I suspect a review is fake?",
      answer:
        'Use the "Report" option beside the review where available. The gloget team can investigate reported reviews and remove content that violates platform guidelines.',
    },
    {
      id: "appointment-reminder",
      category: "notifications",
      question: "Will I receive a reminder before my appointment?",
      answer:
        "If you have opted in, gloget may send appointment reminders by email and/or SMS before your scheduled appointment.",
    },
    {
      id: "notification-preferences",
      category: "notifications",
      question: "How do I manage my notification preferences?",
      answer:
        'Notification preferences can be managed under "Account Settings" > "Notifications," including available email and SMS settings.',
    },
    {
      id: "post-booking-communication",
      category: "notifications",
      question: "What communication will I receive after booking?",
      answer:
        "You may receive a booking confirmation, pre-appointment reminders, and a post-appointment follow-up inviting you to review your experience.",
    },
    {
      id: "list-salon",
      category: "owners",
      question: "How do I list my salon on gloget?",
      answer:
        'Select "For Business" from the homepage and follow the setup process. You will be asked for business details, services, pricing, availability, and photos. Once verified, the listing can be published.',
    },
    {
      id: "listing-fee",
      category: "owners",
      question: "Is there a fee to be listed?",
      answer:
        "gloget offers a free basic listing as well as premium plans with additional features. Refer to the current pricing page for available plans and inclusions.",
    },
    {
      id: "manage-business",
      category: "owners",
      question: "How do I manage my availability and services?",
      answer:
        "Use the business dashboard to manage services, pricing, staff schedules, and availability.",
    },
    {
      id: "respond-reviews",
      category: "owners",
      question: "How do I respond to customer reviews?",
      answer:
        'Sign in to your business dashboard, open "Reviews," and select the option to reply to the customer review.',
    },
    {
      id: "business-payouts",
      category: "owners",
      question: "How are payments transferred to my business?",
      answer:
        "Payments collected through the platform are transferred according to your applicable payout schedule, less any platform fees. Payout and transaction information can be reviewed from the business dashboard.",
    },
    {
      id: "personal-data",
      category: "safety",
      question: "How is my personal data used and stored?",
      answer:
        "Personal data is used to facilitate bookings and improve your experience on gloget. gloget states that it does not sell personal information. See the Privacy Policy for complete details.",
    },
    {
      id: "payment-security",
      category: "safety",
      question: "Is my payment information secure?",
      answer:
        "Payments are processed through secure payment infrastructure. gloget does not store your full card details on its own servers.",
    },
    {
      id: "safety-concern",
      category: "safety",
      question: "How do I report a safety concern about a salon?",
      answer:
        'Contact support using the "Report a Concern" option where available on a salon profile or through the Help Centre. Safety reports are reviewed by the gloget team.',
    },
    {
      id: "site-not-working",
      category: "support",
      question: "The website or app isn't working. What should I do?",
      answer:
        "Try refreshing the page or restarting the app. If the problem continues, clear your browser cache or reinstall the app. You can also check the status page for known outages before contacting support.",
    },
    {
      id: "missing-confirmation",
      category: "support",
      question: "I didn't receive a confirmation email. What should I do?",
      answer:
        'Check your spam or junk folder first. Then open "My Appointments" to confirm whether the booking was completed. If you are still unsure, contact support with your booking details.',
    },
    {
      id: "mobile-platform",
      category: "support",
      question: "Can I use gloget on mobile?",
      answer:
        "Yes. gloget is designed to work on mobile browsers, with mobile app availability described for iOS and Android.",
    },
    {
      id: "contact-support",
      category: "support",
      question: "How do I contact customer support?",
      answer:
        "You can contact gloget support through live chat, the platform chat option, or by email at info@gloget.com.",
    },
  ];

  // =========================================================
  // Filtered FAQ results
  // =========================================================

  filteredFaqs: FaqItem[] = [];

  groupedFaqs: FaqGroup[] = [];

  private readonly categoryOrder: FaqCategoryKey[] = [
    "booking",
    "cancellations",
    "payments",
    "accounts",
    "finding",
    "reviews",
    "notifications",
    "owners",
    "safety",
    "support",
  ];

  // =========================================================
  // Initialization
  // =========================================================

  constructor() {
    this.updateFilteredFaqs();
  }

  // =========================================================
  // Filtering
  // =========================================================

  private updateFilteredFaqs(): void {
    const query = this.searchTerm.trim().toLowerCase();

    this.filteredFaqs = this.faqs.filter((faq) => {
      const categoryMatches =
        this.activeCategory === "all" || faq.category === this.activeCategory;

      if (!categoryMatches) {
        return false;
      }

      if (!query) {
        return true;
      }

      return (
        faq.question.toLowerCase().includes(query) ||
        faq.answer.toLowerCase().includes(query)
      );
    });

    this.groupedFaqs = this.categoryOrder
      .map((key) => ({
        key,
        label: this.getCategoryLabel(key),
        items: this.filteredFaqs.filter((faq) => faq.category === key),
      }))
      .filter((group) => group.items.length > 0);
  }

  // =========================================================
  // Search
  // =========================================================

  onSearchChange(): void {
    this.updateFilteredFaqs();
  }

  clearSearchTerm(): void {
    this.searchTerm = "";
    this.updateFilteredFaqs();
  }

  clearSearch(): void {
    this.searchTerm = "";
    this.activeCategory = "all";
    this.updateFilteredFaqs();
  }

  // =========================================================
  // Categories
  // =========================================================

  setCategory(category: "all" | FaqCategoryKey): void {
    this.activeCategory = category;
    this.updateFilteredFaqs();
  }

  getCategoryCount(category: "all" | FaqCategoryKey): number {
    if (category === "all") {
      return this.faqs.length;
    }

    return this.faqs.filter((faq) => faq.category === category).length;
  }

  getActiveCategoryLabel(): string {
    if (this.activeCategory === "all") {
      return "All questions";
    }

    return this.getCategoryLabel(this.activeCategory);
  }

  private getCategoryLabel(key: FaqCategoryKey): string {
    return (
      this.categories.find((category) => category.key === key)?.label || key
    );
  }

  // =========================================================
  // FAQ accordion
  // =========================================================

  toggleFaq(id: string): void {
    if (this.openFaqIds.has(id)) {
      this.openFaqIds.delete(id);
      return;
    }

    this.openFaqIds.add(id);
  }

  isFaqOpen(id: string): boolean {
    return this.openFaqIds.has(id);
  }

  // =========================================================
  // Angular trackBy
  // =========================================================

  trackByGroup(index: number, group: FaqGroup): string {
    return group.key;
  }

  trackByFaq(index: number, faq: FaqItem): string {
    return faq.id;
  }
}
